import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button/Button';
import type { MergedRubric } from '../../types/merge';
import type { Criterion } from '../../types/evaluation';

// Local type for this component's rubric cells
interface LocalRubricCell {
  aspectIndex: number;
  gradeIndex: number;
  content: string;
}

interface GradeLevel {
  name: string;
  minScore: number;
  maxScore: number;
  color: string;
}

interface RubricMergeProps {
  mergedAspects: Criterion[];
  onComplete: (data: MergedRubric) => void;
  onBack: () => void;
  initialData?: MergedRubric;
}

export const RubricMerge: React.FC<RubricMergeProps> = ({
  mergedAspects,
  onComplete,
  onBack,
  initialData
}) => {
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>([
    { name: '優秀', minScore: 80, maxScore: 100, color: '#10b981' },
    { name: '良好', minScore: 60, maxScore: 79, color: '#3b82f6' },
    { name: '普通', minScore: 40, maxScore: 59, color: '#f59e0b' },
    { name: '要改善', minScore: 0, maxScore: 39, color: '#ef4444' }
  ]);
  
  const [rubricCells, setRubricCells] = useState<LocalRubricCell[]>([]);
  const [userEdited, setUserEdited] = useState(initialData?.userEdited || false);

  // 初期化
  useEffect(() => {
    if (initialData?.rubricData && initialData.rubricData.length > 0) {
      setRubricCells(initialData.rubricData);
    } else {
      generateInitialRubric();
    }
  }, [mergedAspects]);

  const generateInitialRubric = () => {
    const cells: LocalRubricCell[] = [];
    
    mergedAspects.forEach((aspect, aspectIndex) => {
      gradeLevels.forEach((grade, gradeIndex) => {
        cells.push({
          aspectIndex,
          gradeIndex,
          content: generateDefaultContent(aspect, grade, gradeIndex)
        });
      });
    });
    
    setRubricCells(cells);
  };

  const generateDefaultContent = (aspect: Criterion, grade: GradeLevel, gradeIndex: number): string => {
    const descriptions = (aspect.description || []).join('、');
    
    switch (gradeIndex) {
      case 0: // 優秀
        return `${descriptions}について十分に理解し、期待を上回る水準で実現できている`;
      case 1: // 良好
        return `${descriptions}について理解し、期待される水準で実現できている`;
      case 2: // 普通
        return `${descriptions}について基本的な理解があり、最低限の水準で実現できている`;
      case 3: // 要改善
        return `${descriptions}について理解が不足しており、改善が必要である`;
      default:
        return `${descriptions}の評価基準`;
    }
  };

  const updateCellContent = (aspectIndex: number, gradeIndex: number, content: string) => {
    setRubricCells(prev => 
      prev.map(cell => 
        cell.aspectIndex === aspectIndex && cell.gradeIndex === gradeIndex
          ? { ...cell, content }
          : cell
      )
    );
    setUserEdited(true);
  };

  const getCellContent = (aspectIndex: number, gradeIndex: number): string => {
    const cell = rubricCells.find(c => c.aspectIndex === aspectIndex && c.gradeIndex === gradeIndex);
    return cell?.content || '';
  };

  const addGradeLevel = () => {
    const newGrade: GradeLevel = {
      name: '新しい等級',
      minScore: 0,
      maxScore: 100,
      color: '#6b7280'
    };
    setGradeLevels([...gradeLevels, newGrade]);
    
    // 新しい等級用のセルを追加
    const newCells: LocalRubricCell[] = [];
    mergedAspects.forEach((aspect, aspectIndex) => {
      newCells.push({
        aspectIndex,
        gradeIndex: gradeLevels.length,
        content: generateDefaultContent(aspect, newGrade, gradeLevels.length)
      });
    });
    setRubricCells([...rubricCells, ...newCells]);
    setUserEdited(true);
  };

  const removeGradeLevel = (gradeIndex: number) => {
    if (gradeLevels.length <= 2) return; // 最低2つの等級は維持
    
    setGradeLevels(gradeLevels.filter((_, i) => i !== gradeIndex));
    setRubricCells(rubricCells.filter(cell => cell.gradeIndex !== gradeIndex));
    setUserEdited(true);
  };

  const updateGradeLevel = (gradeIndex: number, updates: Partial<GradeLevel>) => {
    setGradeLevels(prev => 
      prev.map((grade, i) => 
        i === gradeIndex ? { ...grade, ...updates } : grade
      )
    );
    setUserEdited(true);
  };

  const handleComplete = () => {
    const analysis = new Map<string, any>();
    analysis.set('gradeLevels', gradeLevels);
    analysis.set('aspectCount', mergedAspects.length);
    analysis.set('gradeCount', gradeLevels.length);
    
    const finalData: MergedRubric = {
      rubricData: rubricCells,
      analysis,
      userEdited
    };
    
    onComplete(finalData);
  };

  return (
    <div className="rubric-merge">
      <div className="merge-header">
        <h2>ルーブリックの統合</h2>
        <p>統合された観点に基づいてルーブリックを手動で調整してください</p>
        <div className="instructions">
          <h4>💡 操作方法</h4>
          <ul>
            <li>統合された観点に基づいてルーブリックが自動生成されます</li>
            <li>各セルの評価基準を直接編集できます</li>
            <li>等級レベルの境界を調整できます</li>
            <li>必要に応じて等級を追加・削除できます</li>
          </ul>
        </div>
      </div>

      <div className="rubric-content">
        <div className="grade-management">
          <h3>等級レベル設定</h3>
          <div className="grade-levels">
            {gradeLevels.map((grade, index) => (
              <div key={index} className="grade-level">
                <input
                  type="text"
                  value={grade.name}
                  onChange={(e) => updateGradeLevel(index, { name: e.target.value })}
                  className="grade-name-input"
                  placeholder="等級名"
                />
                <div className="grade-range">
                  <input
                    type="number"
                    value={grade.minScore}
                    onChange={(e) => updateGradeLevel(index, { minScore: parseInt(e.target.value) || 0 })}
                    className="grade-score-input"
                    placeholder="最小"
                    min="0"
                    max="100"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={grade.maxScore}
                    onChange={(e) => updateGradeLevel(index, { maxScore: parseInt(e.target.value) || 100 })}
                    className="grade-score-input"
                    placeholder="最大"
                    min="0"
                    max="100"
                  />
                </div>
                <input
                  type="color"
                  value={grade.color}
                  onChange={(e) => updateGradeLevel(index, { color: e.target.value })}
                  className="grade-color-input"
                  title="等級の色"
                />
                {gradeLevels.length > 2 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeGradeLevel(index)}
                    className="remove-grade-btn"
                    title="等級を削除"
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button
            variant="ghost"
            onClick={addGradeLevel}
            className="add-grade-btn"
          >
            + 等級を追加
          </Button>
        </div>

        <div className="rubric-table-container">
          <h3>ルーブリック表</h3>
          <div className="rubric-table">
            <div className="rubric-header">
              <div className="rubric-cell rubric-cell--header">観点</div>
              {gradeLevels.map((grade, index) => (
                <div 
                  key={index} 
                  className="rubric-cell rubric-cell--header"
                  style={{ backgroundColor: grade.color, color: 'white' }}
                >
                  <div className="grade-header">
                    <div className="grade-name">{grade.name}</div>
                    <div className="grade-range">({grade.minScore}-{grade.maxScore}点)</div>
                  </div>
                </div>
              ))}
            </div>

            {mergedAspects.map((aspect, aspectIndex) => (
              <div key={aspectIndex} className="rubric-row">
                <div className="rubric-cell rubric-cell--aspect">
                  <div className="aspect-title">{aspect.title}</div>
                  <div className="aspect-description">
                    {(aspect.description || []).join('、')}
                  </div>
                </div>
                {gradeLevels.map((grade, gradeIndex) => (
                  <div key={gradeIndex} className="rubric-cell rubric-cell--editable">
                    <textarea
                      value={getCellContent(aspectIndex, gradeIndex)}
                      onChange={(e) => updateCellContent(aspectIndex, gradeIndex, e.target.value)}
                      className="rubric-cell-textarea"
                      placeholder={`${aspect.title}の${grade.name}レベルの基準を入力...`}
                      rows={3}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="merge-actions">
        <Button variant="ghost" onClick={onBack}>
          戻る
        </Button>
        <Button variant="primary" onClick={handleComplete}>
          次のステップへ
        </Button>
      </div>

      <style>{`
        .rubric-merge {
          padding: 1rem;
        }

        .merge-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .merge-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .merge-header p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .instructions {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-top: 1rem;
        }

        .instructions h4 {
          color: #0c4a6e;
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .instructions ul {
          color: #075985;
          margin: 0;
          padding-left: 1.5rem;
        }

        .instructions li {
          margin-bottom: 0.25rem;
        }

        .rubric-content {
          padding: 1rem 0;
        }

        .grade-management {
          margin-bottom: 2rem;
        }

        .grade-management h3 {
          color: #1f2937;
          font-size: 1.25rem;
          margin: 0 0 1rem 0;
        }

        .grade-levels {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .grade-level {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
        }

        .grade-name-input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 0.875rem;
        }

        .grade-range {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .grade-score-input {
          width: 4rem;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          text-align: center;
        }

        .grade-color-input {
          width: 3rem;
          height: 2rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          cursor: pointer;
        }

        .remove-grade-btn {
          padding: 0.25rem 0.5rem;
          color: #dc2626;
          font-weight: bold;
        }

        .add-grade-btn {
          align-self: flex-start;
          color: #3b82f6;
        }

        .rubric-table-container {
          margin-bottom: 2rem;
        }

        .rubric-table-container h3 {
          color: #1f2937;
          font-size: 1.25rem;
          margin: 0 0 1rem 0;
        }

        .rubric-table {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          overflow: hidden;
          background: white;
        }

        .rubric-header, .rubric-row {
          display: grid;
          grid-template-columns: 200px repeat(auto-fit, minmax(250px, 1fr));
        }

        .rubric-cell {
          padding: 1rem;
          border-right: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }

        .rubric-cell:last-child {
          border-right: none;
        }

        .rubric-row:last-child .rubric-cell {
          border-bottom: none;
        }

        .rubric-cell--header {
          background: #f3f4f6;
          font-weight: 600;
          text-align: center;
        }

        .rubric-cell--aspect {
          background: #fefefe;
          border-right: 2px solid #d1d5db;
        }

        .aspect-title {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .aspect-description {
          font-size: 0.875rem;
          color: #6b7280;
          line-height: 1.4;
        }

        .grade-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .grade-name {
          font-size: 1rem;
          font-weight: 600;
        }

        .grade-range {
          font-size: 0.75rem;
          opacity: 0.9;
        }

        .rubric-cell--editable {
          padding: 0.5rem;
        }

        .rubric-cell-textarea {
          width: 100%;
          min-height: 4rem;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          line-height: 1.4;
          resize: vertical;
          font-family: inherit;
        }

        .rubric-cell-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .rubric-cell-textarea::placeholder {
          color: #9ca3af;
          font-style: italic;
        }

        .merge-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 2rem;
        }

        @media (max-width: 768px) {
          .merge-actions {
            flex-direction: column-reverse;
          }

          .grade-level {
            flex-direction: column;
            align-items: stretch;
            gap: 0.5rem;
          }

          .grade-range {
            justify-content: center;
          }

          .rubric-header, .rubric-row {
            grid-template-columns: 150px repeat(auto-fit, minmax(200px, 1fr));
          }

          .rubric-cell {
            padding: 0.5rem;
          }

          .rubric-table-container {
            overflow-x: auto;
          }

          .rubric-table {
            min-width: 600px;
          }
        }
      `}</style>
    </div>
  );
};